import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  type?: string;
  image?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  jsonLd?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  type = 'website',
  image,
  article,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neuroagents.com.br';
    const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
    const ogImage = image || `${siteUrl}/og-image.jpg`;

    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMeta('description', description);
    updateMeta('keywords', keywords);
    updateMeta('last-modified', new Date().toISOString().split('T')[0]);

    // Open Graph
    updateMeta('og:type', type, true);
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:url', fullCanonical, true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:site_name', 'NeuroAgents Global AI', true);

    // Twitter
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', ogImage);

    // Article specific
    if (article) {
      if (article.publishedTime) {
        updateMeta('article:published_time', article.publishedTime, true);
      }
      if (article.modifiedTime) {
        updateMeta('article:modified_time', article.modifiedTime, true);
      }
      if (article.author) {
        updateMeta('article:author', article.author, true);
      }
      if (article.section) {
        updateMeta('article:section', article.section, true);
      }
      if (article.tags) {
        article.tags.forEach(tag => {
          const tagElement = document.createElement('meta');
          tagElement.setAttribute('property', 'article:tag');
          tagElement.setAttribute('content', tag);
          document.head.appendChild(tagElement);
        });
      }
    }

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = fullCanonical;

    // JSON-LD
    if (jsonLd) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(jsonLd);
    }

    // Cleanup function
    return () => {
      // Remove article tags on unmount
      if (article?.tags) {
        document.querySelectorAll('meta[property="article:tag"]').forEach(el => el.remove());
      }
    };
  }, [title, description, keywords, canonical, type, image, article, jsonLd]);
}

export function generateWebPageSchema(data: {
  name: string;
  description: string;
  url: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neuroagents.com.br';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: data.name,
    description: data.description,
    url: `${siteUrl}${data.url}`,
    publisher: {
      '@type': 'Organization',
      name: 'NeuroAgents Global AI',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
  };
}

export function generateArticleSchema(data: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  url: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neuroagents.com.br';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.headline,
    description: data.description,
    image: data.image,
    datePublished: data.datePublished,
    dateModified: data.dateModified,
    author: {
      '@type': 'Person',
      name: data.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'NeuroAgents Global AI',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${data.url}`,
    },
  };
}

export function generateOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neuroagents.com.br';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'NeuroAgents Global AI',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Sistema de automação de atendimento e follow-up por Inteligência Artificial para WhatsApp Business',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+55-41-99258-4018',
      contactType: 'customer service',
      availableLanguage: 'Portuguese',
    },
    sameAs: [
      'https://www.instagram.com/neuroagents',
      'https://www.facebook.com/neuroagents',
    ],
  };
}

export function generateProductSchema(data: {
  name: string;
  description: string;
  price: string;
  currency?: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neuroagents.com.br';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data.name,
    description: data.description,
    brand: {
      '@type': 'Brand',
      name: 'NeuroAgents Global AI',
    },
    offers: {
      '@type': 'Offer',
      price: data.price,
      priceCurrency: data.currency || 'BRL',
      availability: 'https://schema.org/InStock',
      url: siteUrl,
    },
  };
}

export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function generateBlogPostingSchema(data: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
  url: string;
}) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neuroagents.com.br';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: data.headline,
    description: data.description,
    image: data.image,
    datePublished: data.datePublished,
    dateModified: data.dateModified,
    author: {
      '@type': 'Person',
      name: data.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'NeuroAgents Global AI',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${data.url}`,
    },
  };
}
